// src/components/InvoiceDownload.jsx
// Downloads a clean PDF invoice for a registration
import React, { useState } from 'react';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

function fmtMoney(cents) {
  if (!cents && cents !== 0) return '—';
  return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
}

function fmtDate(val) {
  if (!val) return '—';
  const d = val?.toDate ? val.toDate() : new Date(val?.seconds ? val.seconds * 1000 : val);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

export default function InvoiceDownload({ registration, size = 'md' }) {
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'pt', format: 'letter' });
      const W = doc.internal.pageSize.getWidth();

      // Header bar
      doc.setFillColor(37, 99, 235);
      doc.rect(0, 0, W, 80, 'F');

      // Logo text
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('Scrimly', 40, 35);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Registration Invoice', 40, 52);

      // Invoice # and date top right
      doc.setFontSize(9);
      doc.text(`Invoice: ${registration.id?.slice(0, 8).toUpperCase() ?? '—'}`, W - 40, 35, { align: 'right' });
      doc.text(`Date: ${fmtDate(registration.createdAt)}`, W - 40, 50, { align: 'right' });

      let y = 110;

      // Team + Season info
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(registration.teamName || '—', 40, y);
      y += 20;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(registration.seasonName || '—', 40, y);
      y += 30;

      // Divider
      doc.setDrawColor(241, 245, 249);
      doc.setLineWidth(1);
      doc.line(40, y, W - 40, y);
      y += 20;

      // Player / Parent info
      const infoRows = [
        ['Player',    registration.playerName || '—'],
        ['Division',  registration.division   || '—'],
        ['Parent',    registration.parentName || '—'],
        ['Email',     registration.parentEmail || '—'],
        ['Phone',     registration.parentPhone || '—'],
      ];
      doc.setFontSize(10);
      infoRows.forEach(([label, value]) => {
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(label, 40, y);
        doc.setTextColor(15, 23, 42);
        doc.setFont('helvetica', 'bold');
        doc.text(value, 200, y);
        y += 18;
      });

      y += 12;
      doc.setDrawColor(241, 245, 249);
      doc.line(40, y, W - 40, y);
      y += 20;

      // Pricing breakdown
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Pricing Breakdown', 40, y);
      y += 18;

      const p = registration.pricing || {};
      const priceRows = [
        ['Base price', fmtMoney(p.baseAmount)],
        p.earlyBirdSaving > 0 ? ['Early bird discount', `\u2212${fmtMoney(p.earlyBirdSaving)}`] : null,
        p.siblingDiscount > 0 ? ['Sibling discount', `\u2212${fmtMoney(p.siblingDiscount)}`]   : null,
        p.returningDiscount > 0 ? ['Returning player discount', `\u2212${fmtMoney(p.returningDiscount)}`] : null,
        p.financialAidDiscount > 0 ? ['Financial aid', `\u2212${fmtMoney(p.financialAidDiscount)}`] : null,
      ].filter(Boolean);

      priceRows.forEach(([label, value]) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(label, 60, y);
        const isDiscount = value.startsWith('\u2212');
        if (isDiscount) {
          doc.setTextColor(22, 163, 74);
        } else {
          doc.setTextColor(15, 23, 42);
        }
        doc.setFont('helvetica', 'bold');
        doc.text(value, W - 40, y, { align: 'right' });
        y += 17;
      });

      // Total line
      y += 4;
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(40, y - 8, W - 80, 26, 4, 4, 'F');
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42);
      doc.text('Total', 55, y + 9);
      doc.setTextColor(37, 99, 235);
      doc.text(fmtMoney(p.finalAmount), W - 55, y + 9, { align: 'right' });
      y += 36;

      // Payment plan schedule
      if (registration.paymentPlan && registration.installmentsTotal > 1) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        doc.text('Payment Schedule', 40, y);
        y += 18;

        const { firstInstallment, installmentAmount, installments } = registration.paymentPlan;
        const schedule = [
          { label: 'Payment 1 (today)', amount: firstInstallment },
          ...Array.from({ length: installments - 1 }, (_, i) => ({
            label: `Payment ${i + 2}`,
            amount: installmentAmount,
          })),
        ];
        schedule.forEach((item, i) => {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text(item.label, 60, y);
          if (i === 0) {
            doc.setTextColor(37, 99, 235);
            doc.setFont('helvetica', 'bold');
          } else {
            doc.setTextColor(15, 23, 42);
            doc.setFont('helvetica', 'normal');
          }
          doc.text(fmtMoney(item.amount), W - 40, y, { align: 'right' });
          y += 17;
        });
        y += 10;
      }

      // Waiver signature
      if (registration.waiverSignedByName) {
        doc.setDrawColor(241, 245, 249);
        doc.line(40, y, W - 40, y);
        y += 20;
        doc.setFontSize(9);
        doc.setTextColor(100, 116, 139);
        doc.setFont('helvetica', 'normal');
        doc.text(`Waiver signed by: ${registration.waiverSignedByName}  \u00b7  ${fmtDate(registration.waiverSignedAt)}`, 40, y);
        y += 14;
        if (registration.waiversSigned?.length > 0) {
          doc.text(`Documents signed: ${registration.waiversSigned.join(', ')}`, 40, y);
        }
      }

      // Footer
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFillColor(241, 245, 249);
      doc.rect(0, pageH - 40, W, 40, 'F');
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text('Generated by Scrimly \u00b7 getscrimly.com', W / 2, pageH - 16, { align: 'center' });

      const playerName = (registration.playerName || 'registration').replace(/\s+/g, '-').toLowerCase();
      doc.save(`scrimly-invoice-${playerName}.pdf`);
    } catch (err) {
      console.error('[InvoiceDownload]', err);
    } finally {
      setLoading(false);
    }
  };

  const sizeClass = size === 'sm'
    ? 'px-3 py-2 text-xs gap-1.5'
    : 'px-5 py-2.5 text-sm gap-2';

  return (
    <button
      onClick={generate}
      disabled={loading}
      className={`inline-flex items-center ${sizeClass} rounded-xl border-2 border-slate-200 font-semibold text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50`}
    >
      {loading
        ? <ArrowPathIcon className="h-4 w-4 animate-spin" />
        : <ArrowDownTrayIcon className="h-4 w-4" />}
      {loading ? 'Generating\u2026' : 'Download Invoice PDF'}
    </button>
  );
}
